import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import { Diff } from 'deep-diff'
import useToggle from 'hooks/useToggle'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'
import ExpandableObjectView from './ExpandableObjectView'

const stringable = new Set(['boolean', 'number', 'undefined'])
const toStringable = new Set(['symbol', 'function'])

const ToggleClickable = styled(Clickable)`
  position: absolute;
  left: ${rem(-20)};
  top: ${rem(1)};
  overflow: hidden;

  & svg {
    transform: rotate(-90deg);
    transition: transform 0.2s;
    margin-block: ${rem(-4)};
  }

  &.isExpanded svg {
    transform: rotate(0);
  }
`

const addedStyles = css`
  background-color: rgba(0, 128, 0, 0.15);
  color: #228b22;
`

const removedStyles = css`
  background-color: rgba(255, 0, 0, 0.15);
  color: #dc143c;
`

const MonospaceTextBlock = styled.div`
  font-family: monospace;
  padding-left: 2ch;
  position: relative;
  text-indent: -2ch;
  word-break: break-word;
`

const ObjKey = styled.span`
  font-weight: bold;
`

const AddedInlineValue = styled.span`
  ${addedStyles}
`

const RemovedInlineValue = styled.span`
  ${removedStyles}
`

const AddedWrapper = styled.div`
  ${addedStyles}
`

const RemovedWrapper = styled.div`
  ${removedStyles}
`

function formatPrimitive(value: unknown): string {
  if (value === null || stringable.has(typeof value)) {
    return String(value)
  }
  if (toStringable.has(typeof value)) {
    return (value as { toString(): string }).toString()
  }
  if (typeof value === 'string') {
    return JSON.stringify(value)
  }
  return 'unknown'
}

export interface ProcessedDiffs {
  directChange: Diff<unknown> | undefined
  changedKeys: Array<string | number>
  childDiffsByKey: Map<string | number, Array<Diff<unknown>>>
  isArray: boolean
}

// Process all diffs into a structure we can use for rendering
export function processDiffs(diffs: Array<Diff<unknown>>): ProcessedDiffs {
  const childDiffsByKey = new Map<string | number, Array<Diff<unknown>>>()
  let directChange: Diff<unknown> | undefined
  let isArray = false

  for (const d of diffs) {
    if (d.kind === 'A' && (!d.path || d.path.length === 0)) {
      // Array element change at this level - group by index
      isArray = true
      const childDiff = {
        ...d.item,
        path: d.item.path ?? [],
      }

      const existing = childDiffsByKey.get(d.index)
      if (existing) {
        existing.push(childDiff)
      } else {
        childDiffsByKey.set(d.index, [childDiff])
      }
    } else if (!d.path || d.path.length === 0) {
      // Direct change at this level (N, D, E)
      directChange = d
    } else {
      // Nested change - group by first path segment
      const key = d.path[0]
      const childDiff = {
        ...d,
        path: d.path.slice(1),
      }

      const existing = childDiffsByKey.get(key)
      if (existing) {
        existing.push(childDiff)
      } else {
        childDiffsByKey.set(key, [childDiff])
      }
    }
  }

  // Sort keys - numerically for arrays, as strings for objects
  const changedKeys = Array.from(childDiffsByKey.keys())
  if (isArray) {
    changedKeys.sort((a, b) => (a as number) - (b as number))
  } else {
    changedKeys.sort((a, b) => String(a).localeCompare(String(b)))
  }

  return {
    directChange,
    changedKeys,
    childDiffsByKey,
    isArray,
  }
}

interface DiffViewProps {
  diffs: Array<Diff<unknown>>
  objKey?: string
  initialExpanded?: true
}

function ExpandableDiffView({ diffs, objKey, initialExpanded }: DiffViewProps) {
  const { value: isExpanded, toggle: toggleExpanded } = useToggle(initialExpanded)

  const { directChange, changedKeys, childDiffsByKey, isArray } = useMemo(
    () => processDiffs(diffs),
    [diffs],
  )

  const keyPrefix = objKey !== undefined ? <ObjKey>{objKey}: </ObjKey> : null

  const toggleButton = (
    <ToggleClickable onClick={toggleExpanded} className={clsx({ isExpanded })}>
      <LineAngleDownIcon size={20} colour="neutral-four" />
    </ToggleClickable>
  )

  if (diffs.length === 0) {
    return null
  }

  // Handle direct changes (N, D, E at this path) - 'A' diffs are handled via childDiffsByKey
  if (directChange) {
    switch (directChange.kind) {
      case 'N': // New value added
        return (
          <AddedWrapper>
            <ExpandableObjectView value={directChange.rhs} objKey={objKey} initialExpanded={initialExpanded} />
          </AddedWrapper>
        )

      case 'D': // Value deleted
        return (
          <RemovedWrapper>
            <ExpandableObjectView value={directChange.lhs} objKey={objKey} initialExpanded={initialExpanded} />
          </RemovedWrapper>
        )

      case 'E': // Value edited (primitive change)
        return (
          <MonospaceTextBlock>
            {keyPrefix}
            <RemovedInlineValue>{formatPrimitive(directChange.lhs)}</RemovedInlineValue>
            {' → '}
            <AddedInlineValue>{formatPrimitive(directChange.rhs)}</AddedInlineValue>
          </MonospaceTextBlock>
        )
    }
  }

  // Nested changes in an object or array
  if (changedKeys.length === 0) {
    return null
  }

  const openBrace = isArray ? '[' : '{'
  const closeBrace = isArray ? ']' : '}'
  const collapsedBrace = isArray ? '[ ... ]' : '{ ... }'

  return (
    <MonospaceTextBlock>
      {toggleButton}
      {!isExpanded && <div>{keyPrefix}{collapsedBrace}</div>}
      {isExpanded && <>
        <div>{keyPrefix}{openBrace}</div>
        {changedKeys.map((key) => (
          <ExpandableDiffView
            key={String(key)}
            diffs={childDiffsByKey.get(key)!}
            objKey={String(key)}
          />
        ))}
        <div>{closeBrace}</div>
      </>}
    </MonospaceTextBlock>
  )
}

export default ExpandableDiffView
